import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import {
  Phone, MapPin, Clock, Star, Mail, Wrench, Droplets, Flame, Hammer,
  ShieldCheck, BadgeCheck, AlertTriangle, ChevronLeft, ChevronRight,
  Check, ArrowRight, ArrowLeft, Search, Menu, X,
} from 'lucide-react';

const SERVICE_ICONS = {
  'Emergency Repair': AlertTriangle,
  'Drain & Sewer': Droplets,
  'Water Heaters': Flame,
  'Leak Detection': Search,
  'Remodels & New Construction': Hammer,
  'Commercial Service': ShieldCheck,
};

const PILLARS = [
  {
    icon: BadgeCheck,
    title: 'Licensed Master Plumber',
    body: 'WA license #APEXPP814R3. $2M general liability. A master plumber on every job — not a tech with the company\'s license number on his truck.',
  },
  {
    icon: Wrench,
    title: 'Up-Front Pricing',
    body: "Written quote before we turn a wrench. The number you see is the number you pay. No surprise upcharges, no commission selling, no scripted upsell.",
  },
  {
    icon: Clock,
    title: '24/7 Real Dispatch',
    body: "Tom answers the phone — nights, weekends, holidays. Average on-site within 60 minutes anywhere in the Tri-Cities. No call centers.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// Single source of truth for the keyframes used by inline-styled effects below.
// Scoped to this component via a unique class prefix; safe to inject once.
const SHEEN_STYLE = `
  @keyframes apx-sheen { 0% { transform: translateX(-120%); } 60%, 100% { transform: translateX(220%); } }
  .apx-cta-sheen { position: relative; overflow: hidden; isolation: isolate; }
  .apx-cta-sheen::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%);
    transform: translateX(-120%);
    animation: apx-sheen 5.5s ease-in-out infinite;
    animation-delay: 1.2s;
    pointer-events: none;
  }
  @keyframes apx-bubble {
    0%   { transform: translateY(20px) scale(0.9); opacity: 0; }
    20%  { opacity: var(--bub-op, 0.5); }
    80%  { opacity: var(--bub-op, 0.5); }
    100% { transform: translateY(-120vh) scale(1.15); opacity: 0; }
  }
  .apx-bubble { animation: apx-bubble var(--bub-dur, 14s) linear infinite; }
  @keyframes apx-wave-drift {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .apx-wave-drift { animation: apx-wave-drift 22s linear infinite; will-change: transform; }
  @media (prefers-reduced-motion: reduce) {
    .apx-cta-sheen::after, .apx-bubble, .apx-wave-drift { animation: none !important; }
  }
`;

function useCountUp(target, { active, duration = 1400 } = {}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (target === 0) { setValue(0); return undefined; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return value;
}

function parseStat(value) {
  const m = String(value).replace(/,/g, '').match(/^(-?\d+(?:\.\d+)?)(.*)$/);
  if (!m) return null;
  return { num: parseFloat(m[1]), suffix: m[2] || '' };
}

function ApexPlumbingDemo({ demo, sample = false }) {
  const [zip, setZip] = useState('');
  const [zipResult, setZipResult] = useState(null);
  const [tIndex, setTIndex] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const phoneRaw = demo.contact.phone.replace(/[^\d+]/g, '');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!demo.testimonials?.length) return undefined;
    const id = setInterval(() => {
      setTIndex((i) => (i + 1) % demo.testimonials.length);
    }, 6500);
    return () => clearInterval(id);
  }, [demo.testimonials]);

  function checkZip(e) {
    e.preventDefault();
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) {
      setZipResult({ ok: false, msg: 'Enter a 5-digit ZIP code.' });
      return;
    }
    const inArea = z.startsWith(demo.serviceZipPrefix || '993');
    setZipResult(
      inArea
        ? { ok: true, msg: "Yes — we service your area. Call now for a quote." }
        : { ok: false, msg: "Just outside our standard service area. Call — we may still be able to help." },
    );
  }

  return (
    <div className="bg-white text-slate-900 font-sans">
      <style>{SHEEN_STYLE}</style>
      <SudcoBanner sample={sample} demoName={demo.name} demoIndustry={demo.industry} />

      <TopBar phone={demo.contact.phone} phoneRaw={phoneRaw} address={demo.contact.address} />

      <Header
        demoName={demo.name}
        phone={demo.contact.phone}
        phoneRaw={phoneRaw}
        scrolled={scrolled}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
      />

      <Hero
        demo={demo}
        zip={zip}
        setZip={setZip}
        zipResult={zipResult}
        checkZip={checkZip}
      />

      <Pillars />

      <Services services={demo.services} />

      <About about={demo.about} cover={demo.gallery?.[0]} />

      <Pricing featured={demo.featured} />

      <Testimonials testimonials={demo.testimonials} index={tIndex} setIndex={setTIndex} />

      <ServiceArea cities={demo.serviceAreas || []} />

      <EmergencyStrip phone={demo.contact.phone} phoneRaw={phoneRaw} />

      <Contact contact={demo.contact} hours={demo.hours} />

      <Footer demo={demo} />
    </div>
  );
}

function SudcoBanner({ sample, demoName, demoIndustry }) {
  return (
    <div className="fixed bottom-5 right-5 z-50">
      <div className="bg-[#0B0F19]/90 border border-[#2A3142] backdrop-blur-md rounded-full px-5 py-3 shadow-[0_0_60px_-15px_rgba(99,102,241,0.5)] flex items-center gap-4 text-sm">
        <span className="text-[#8B92A8] hidden sm:inline">
          {sample
            ? <>Like this? Get one for your <span className="text-[#F5F6FA] font-medium">{demoIndustry || 'business'}</span> — <span className="text-[#6366F1] font-semibold">$800</span></>
            : <>Free demo for <span className="text-[#F5F6FA] font-medium">{demoName}</span> by <span className="text-[#6366F1] font-semibold">Sudco Solutions</span></>
          }
        </span>
        <Link to="/" className="flex items-center gap-1.5 text-[#F5F6FA] font-semibold hover:text-[#06B6D4] transition-colors whitespace-nowrap">
          <ArrowLeft size={14} /> Back to Sudco
        </Link>
      </div>
    </div>
  );
}

function TopBar({ phone, phoneRaw, address }) {
  return (
    <div className="hidden md:block bg-slate-900 text-slate-300 text-xs">
      <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={12} className="text-sky-400" />{address}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <BadgeCheck size={12} className="text-sky-400" />WA Lic. #APEXPP814R3
          </span>
        </div>
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5">
            <Clock size={12} className="text-sky-400" />24 / 7 Emergency Dispatch
          </span>
          <a href={`tel:${phoneRaw}`} className="inline-flex items-center gap-1.5 text-white font-semibold hover:text-sky-300">
            <Phone size={12} className="text-sky-400" />{phone}
          </a>
        </div>
      </div>
    </div>
  );
}

function Header({ demoName, phone, phoneRaw, scrolled, navOpen, setNavOpen }) {
  const links = [
    { href: '#services', label: 'Services' },
    { href: '#about', label: 'About' },
    { href: '#pricing', label: 'Pricing' },
    { href: '#reviews', label: 'Reviews' },
    { href: '#areas', label: 'Service Area' },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur shadow-[0_1px_0_0_rgba(0,0,0,0.06),0_8px_24px_-12px_rgba(0,0,0,0.12)]'
          : 'bg-white border-b border-slate-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2.5">
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-white shadow-sm">
            <Droplets size={18} />
          </span>
          <span className="font-display font-extrabold text-lg md:text-xl tracking-tight text-slate-900">
            {demoName}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold text-slate-700">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="relative group">
              <span className="group-hover:text-sky-600 transition-colors">{l.label}</span>
              <span className="absolute left-0 -bottom-1 h-0.5 w-full origin-left scale-x-0 bg-sky-600 transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${phoneRaw}`}
            className="inline-flex items-center gap-2 text-sky-700 font-bold text-base hover:text-sky-900"
          >
            <Phone size={16} />{phone}
          </a>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold shadow-sm hover:shadow-md transition-all"
          >
            Get a Quote <ArrowRight size={14} />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          className="lg:hidden p-2 -mr-2 text-slate-700"
        >
          {navOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {navOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-slate-100 bg-white"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setNavOpen(false)}
                  className="px-3 py-2.5 rounded-md font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-2 grid grid-cols-2 gap-2">
                <a
                  href={`tel:${phoneRaw}`}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-sky-600 text-sky-700 text-sm font-bold"
                >
                  <Phone size={14} />Call
                </a>
                <a
                  href="#contact"
                  onClick={() => setNavOpen(false)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-sky-600 text-white text-sm font-bold"
                >
                  Get a Quote
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero({ demo, zip, setZip, zipResult, checkZip }) {
  return (
    <section id="top" className="relative overflow-hidden bg-slate-900 text-white">
      <div className="absolute inset-0">
        <img
          src={demo.cover}
          alt=""
          className="w-full h-full object-cover opacity-40"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-900/70 to-sky-900/60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(14,165,233,0.25),transparent_55%)]" />
        <BubbleField />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-28">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/15 border border-sky-400/30 text-sky-200 text-xs font-bold uppercase tracking-wider mb-5"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-400" />
              </span>
              Available 24/7 — {demo.location}
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight mb-5"
            >
              Tri-Cities Plumbing
              <span className="block text-sky-400">Done Right, Done Once.</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-lg text-slate-300 leading-relaxed max-w-xl mb-7">
              {demo.tagline}
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8 text-sm text-slate-300">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                  ))}
                </span>
                <strong className="text-white">4.9</strong> · 200+ Google reviews
              </span>
              <span className="inline-flex items-center gap-1.5"><BadgeCheck size={14} className="text-sky-400" /> Licensed & insured</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-sky-400" /> 1-yr warranty</span>
            </motion.div>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              <a
                href={`tel:${demo.contact.phone.replace(/[^\d+]/g, '')}`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-base font-bold shadow-lg shadow-sky-900/40 transition-all hover:scale-[1.02]"
              >
                <Phone size={18} />Call {demo.contact.phone}
              </a>
              <a
                href="#services"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-white/10 hover:bg-white/15 backdrop-blur text-white text-base font-semibold border border-white/20"
              >
                See our services <ArrowRight size={16} />
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white/95 backdrop-blur text-slate-900 rounded-2xl shadow-2xl shadow-black/30 p-6 md:p-7 border border-white/30"
          >
            <div className="text-xs font-bold uppercase tracking-wider text-sky-600 mb-1.5">Service Area Check</div>
            <h2 className="font-display font-extrabold text-2xl mb-4 leading-tight">
              Do we serve your home?
            </h2>
            <form onSubmit={checkZip} className="space-y-3">
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">Enter your ZIP code</span>
                <div className="mt-1.5 flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{5}"
                    maxLength={5}
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
                    placeholder="99301"
                    className="flex-1 min-w-0 px-4 py-3 rounded-lg border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 focus:outline-none text-base font-medium"
                  />
                  <button
                    type="submit"
                    className="inline-flex items-center gap-1.5 px-5 py-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-bold text-sm whitespace-nowrap"
                  >
                    Check <ArrowRight size={14} />
                  </button>
                </div>
              </label>
              <AnimatePresence mode="wait">
                {zipResult && (
                  <motion.div
                    key={zipResult.msg}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                    className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                      zipResult.ok ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'
                    }`}
                  >
                    {zipResult.ok ? (
                      <Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    )}
                    <span>{zipResult.msg}</span>
                  </motion.div>
                )}
              </AnimatePresence>
              <p className="text-xs text-slate-500">
                Tri-Cities, WA: Pasco, Kennewick, Richland, West Richland, Burbank, Finley.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
      <WaveDivider />
    </section>
  );
}

function BubbleField() {
  // Stable per-mount set of bubbles. Positions, sizes, durations and delays
  // are randomized once so they don't reshuffle on re-render.
  const reduce = useReducedMotion();
  const bubbles = React.useMemo(() => {
    if (reduce) return [];
    return Array.from({ length: 14 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: 4 + Math.random() * 12,
      duration: 12 + Math.random() * 10,
      delay: -Math.random() * 18,
      opacity: 0.18 + Math.random() * 0.32,
    }));
  }, [reduce]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {bubbles.map((b) => (
        <span
          key={b.id}
          className="apx-bubble absolute bottom-0 rounded-full bg-sky-300"
          style={{
            left: `${b.left}%`,
            width: b.size,
            height: b.size,
            '--bub-dur': `${b.duration}s`,
            '--bub-op': b.opacity,
            animationDelay: `${b.delay}s`,
            boxShadow: 'inset -1px -2px 2px rgba(255,255,255,0.4), 0 0 8px rgba(125,211,252,0.35)',
          }}
        />
      ))}
    </div>
  );
}

function WaveDivider() {
  // Two layered SVG paths that drift horizontally at different speeds for a
  // gentle parallax. Each path is rendered twice end-to-end so the loop is
  // seamless when the front copy translates -50%.
  return (
    <div className="absolute inset-x-0 bottom-0 h-16 md:h-20 pointer-events-none" aria-hidden="true">
      <div className="apx-wave-drift absolute bottom-0 left-0 w-[200%] h-full opacity-50" style={{ animationDuration: '32s' }}>
        <svg className="w-1/2 h-full inline-block" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,60 C200,100 400,20 600,60 C800,100 1000,20 1200,60 L1200,120 L0,120 Z" fill="#ffffff" fillOpacity="0.5" />
        </svg>
        <svg className="w-1/2 h-full inline-block -ml-px" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,60 C200,100 400,20 600,60 C800,100 1000,20 1200,60 L1200,120 L0,120 Z" fill="#ffffff" fillOpacity="0.5" />
        </svg>
      </div>
      <div className="apx-wave-drift absolute bottom-0 left-0 w-[200%] h-full">
        <svg className="w-1/2 h-full inline-block" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,80 C150,40 350,110 600,70 C850,30 1050,100 1200,70 L1200,120 L0,120 Z" fill="#ffffff" />
        </svg>
        <svg className="w-1/2 h-full inline-block -ml-px" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,80 C150,40 350,110 600,70 C850,30 1050,100 1200,70 L1200,120 L0,120 Z" fill="#ffffff" />
        </svg>
      </div>
    </div>
  );
}

function Pillars() {
  return (
    <section className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeUp}
                transition={{ delay: i * 0.08 }}
                className="group relative"
              >
                <div className="flex flex-col items-start">
                  <div className="w-14 h-14 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center mb-4 ring-1 ring-sky-100 group-hover:bg-sky-600 group-hover:text-white group-hover:ring-sky-700 transition-all">
                    <Icon size={26} strokeWidth={2} />
                  </div>
                  <h3 className="font-display font-extrabold text-xl text-slate-900 mb-2 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-[15px]">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Services({ services }) {
  return (
    <section id="services" className="py-16 md:py-24 bg-slate-50 border-y border-slate-200/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-2">What we do</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 leading-tight">
            Plumbing solutions for homes & businesses
          </h2>
          <p className="text-slate-600 mt-3 leading-relaxed">
            From a slow drip to a flooded basement — one trusted crew, one written quote, one year warranty on every repair.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = SERVICE_ICONS[s.title] || Wrench;
            return (
              <motion.a
                key={s.title}
                href="#contact"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                transition={{ delay: (i % 3) * 0.06 }}
                className="group relative bg-white rounded-2xl p-6 border border-slate-200 hover:border-sky-300 hover:shadow-xl hover:shadow-sky-900/5 transition-all hover:-translate-y-0.5"
              >
                <div className="absolute top-0 left-6 right-6 h-0.5 bg-gradient-to-r from-sky-500 to-sky-700 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-400" />
                <div className="w-12 h-12 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center mb-4 group-hover:bg-sky-600 group-hover:text-white transition-colors">
                  <Icon size={22} />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900 mb-1.5 leading-tight">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{s.desc}</p>
                <span className="inline-flex items-center gap-1 text-sky-700 font-semibold text-sm group-hover:gap-2 transition-all">
                  Learn more <ArrowRight size={14} />
                </span>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function About({ about, cover }) {
  return (
    <section id="about" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-2">Owner-Operator Since 2017</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 leading-tight mb-5">
            {about.heading}
          </h2>
          {about.body.split(/\n\n+/).map((para, i) => (
            <p key={i} className="text-slate-600 leading-relaxed mb-4 text-[15.5px]">{para}</p>
          ))}

          <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-slate-200">
            <Stat value="8+" label="Years on the truck" />
            <Stat value="4,000+" label="Jobs completed" />
            <Stat value="97%" label="On-time arrival" />
          </div>

          {about.signature && (
            <p className="mt-7 font-semibold text-slate-700">— {about.signature}</p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute -top-4 -left-4 w-24 h-24 rounded-2xl bg-sky-100 -z-0" />
          <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-2xl bg-amber-50 -z-0" />
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/10">
            <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="absolute -bottom-6 left-6 right-6 lg:left-auto lg:right-6 lg:max-w-xs bg-white rounded-xl shadow-2xl shadow-slate-900/20 p-5 border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-white font-bold text-lg">
                TR
              </div>
              <div>
                <div className="font-bold text-slate-900 text-sm">Tom Reyes</div>
                <div className="text-xs text-slate-500">Master Plumber · WA #APEXPP814R3</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduce = useReducedMotion();
  const parsed = parseStat(value);
  const animated = useCountUp(parsed?.num ?? 0, { active: inView && !reduce });
  const display = !parsed
    ? value
    : reduce
      ? value
      : `${formatStat(animated, parsed.num)}${parsed.suffix}`;
  return (
    <div ref={ref}>
      <div className="font-display font-extrabold text-2xl md:text-3xl text-sky-700 tabular-nums">{display}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function formatStat(current, target) {
  // Match formatting of the target so the digit count doesn't shift mid-animation:
  // integer targets render as integers; fractional targets keep one decimal.
  if (Number.isInteger(target)) {
    const n = Math.round(current);
    return target >= 1000 ? n.toLocaleString('en-US') : String(n);
  }
  return current.toFixed(1);
}

function Pricing({ featured }) {
  if (!featured?.items?.length) return null;
  return (
    <section id="pricing" className="py-16 md:py-24 bg-slate-50 border-y border-slate-200/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="max-w-2xl mb-12"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-2">{featured.kicker || 'Common pricing'}</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 leading-tight mb-3">
            {featured.heading}
          </h2>
          {featured.subheading && (
            <p className="text-slate-600 leading-relaxed">{featured.subheading}</p>
          )}
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {featured.items.map((item, i) => (
            <motion.div
              key={item.name}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              transition={{ delay: (i % 3) * 0.06 }}
              className="bg-white rounded-xl p-5 border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all"
            >
              <div className="flex justify-between items-start gap-3 mb-2">
                <h3 className="font-display font-bold text-base text-slate-900 leading-tight">{item.name}</h3>
                {item.price && (
                  <span className="font-display font-extrabold text-sky-700 text-base whitespace-nowrap">{item.price}</span>
                )}
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              {item.tag && (
                <span className="inline-block mt-3 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                  {item.tag}
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ testimonials, index, setIndex }) {
  if (!testimonials?.length) return null;
  const t = testimonials[index];
  const total = testimonials.length;
  return (
    <section id="reviews" className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-2">Reviews</p>
        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 leading-tight mb-10">
          What our neighbors say
        </h2>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              className="px-2"
            >
              <div className="flex justify-center gap-0.5 mb-5">
                {Array.from({ length: t.rating || 5 }).map((_, i) => (
                  <Star key={i} size={20} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="font-display text-xl md:text-2xl leading-snug text-slate-800 max-w-3xl mx-auto mb-5">
                &ldquo;{t.quote}&rdquo;
              </p>
              <p className="text-slate-500 text-sm font-medium">— {t.author}</p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-center gap-3 mt-8">
            <button
              type="button"
              onClick={() => setIndex((i) => (i - 1 + total) % total)}
              aria-label="Previous review"
              className="w-10 h-10 rounded-full border border-slate-300 hover:border-sky-500 hover:text-sky-600 flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex gap-1.5">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Review ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    i === index ? 'w-6 bg-sky-600' : 'w-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => setIndex((i) => (i + 1) % total)}
              aria-label="Next review"
              className="w-10 h-10 rounded-full border border-slate-300 hover:border-sky-500 hover:text-sky-600 flex items-center justify-center transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceArea({ cities }) {
  if (!cities.length) return null;
  return (
    <section id="areas" className="py-14 md:py-16 bg-slate-50 border-y border-slate-200/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-2">Where we work</p>
        <h2 className="font-display font-extrabold text-2xl md:text-3xl text-slate-900 leading-tight mb-6">
          Serving the Tri-Cities & surrounding areas
        </h2>
        <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
          {cities.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 shadow-sm"
            >
              <MapPin size={12} className="text-sky-600" />{c}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function EmergencyStrip({ phone, phoneRaw }) {
  return (
    <section className="relative bg-gradient-to-r from-rose-600 via-red-600 to-rose-700 text-white overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(255,255,255,0.15),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12 flex flex-col md:flex-row items-center justify-between gap-5">
        <div className="flex items-center gap-4 text-center md:text-left">
          <AlertTriangle size={36} className="hidden sm:block flex-shrink-0" />
          <div>
            <h3 className="font-display font-extrabold text-2xl md:text-3xl leading-tight">
              Got a leak right now?
            </h3>
            <p className="text-rose-100 text-sm md:text-base mt-1">We dispatch in under an hour, 24 / 7 / 365.</p>
          </div>
        </div>
        <a
          href={`tel:${phoneRaw}`}
          className="apx-cta-sheen inline-flex items-center gap-3 px-7 py-4 rounded-xl bg-white text-rose-700 font-extrabold text-lg shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all whitespace-nowrap"
        >
          <Phone size={20} />Call {phone}
        </a>
      </div>
    </section>
  );
}

function Contact({ contact, hours }) {
  const phoneRaw = contact.phone.replace(/[^\d+]/g, '');
  return (
    <section id="contact" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-2">Reach out</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 leading-tight mb-6">
            Get a free quote
          </h2>
          <p className="text-slate-600 mb-7 leading-relaxed">
            Send us a message or call directly. We'll get back to you within an hour during business days, faster for emergencies.
          </p>

          <div className="space-y-4">
            <ContactRow
              icon={Phone}
              href={`tel:${phoneRaw}`}
              label="Call dispatch"
              value={contact.phone}
            />
            <ContactRow
              icon={Mail}
              href={`mailto:${contact.email}`}
              label="Email"
              value={contact.email}
            />
            <ContactRow
              icon={MapPin}
              label="Office"
              value={contact.address}
            />
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-2">Hours</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-slate-900 leading-tight mb-6">
            When we're open
          </h2>
          <div className="rounded-2xl border border-slate-200 overflow-hidden">
            {hours.map((h, i) => (
              <div
                key={h.day}
                className={`flex items-center justify-between px-5 py-4 ${
                  i % 2 === 0 ? 'bg-slate-50' : 'bg-white'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-semibold text-slate-800">
                  <Clock size={16} className="text-sky-600" />{h.day}
                </span>
                <span className="text-slate-600 font-medium">{h.time}</span>
              </div>
            ))}
          </div>
          <a
            href={`tel:${phoneRaw}`}
            className="mt-5 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-bold shadow-md hover:shadow-lg transition-all"
          >
            <Phone size={16} />Call now: {contact.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, href, label, value }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <span className="w-11 h-11 flex-shrink-0 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center group-hover:bg-sky-600 group-hover:text-white transition-colors">
        <Icon size={18} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-0.5">{label}</div>
        <div className="text-slate-900 font-semibold">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block">{inner}</a>
  ) : (
    <div>{inner}</div>
  );
}

function Footer({ demo }) {
  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-white">
              <Droplets size={16} />
            </span>
            <span className="font-display font-extrabold text-lg text-white">{demo.name}</span>
          </div>
          <p className="text-sm leading-relaxed text-slate-400 mb-4">
            Owner-operated plumbing service for the Tri-Cities since 2017. Licensed, bonded, insured.
          </p>
          <div className="text-xs text-slate-500">WA Lic. #APEXPP814R3</div>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Services</h4>
          <ul className="space-y-2 text-sm">
            {demo.services.map((s) => (
              <li key={s.title}>
                <a href="#services" className="hover:text-sky-400 transition-colors">{s.title}</a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Contact</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <Phone size={14} className="text-sky-400 mt-0.5 flex-shrink-0" />
              <a href={`tel:${demo.contact.phone.replace(/[^\d+]/g, '')}`} className="hover:text-sky-400">{demo.contact.phone}</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={14} className="text-sky-400 mt-0.5 flex-shrink-0" />
              <a href={`mailto:${demo.contact.email}`} className="hover:text-sky-400 break-all">{demo.contact.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin size={14} className="text-sky-400 mt-0.5 flex-shrink-0" />
              <span>{demo.contact.address}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <p>&copy; {new Date().getFullYear()} {demo.name}. All rights reserved.</p>
          <p>
            Demo site built by{' '}
            <Link to="/" className="text-sky-400 font-semibold hover:text-sky-300">Sudco Solutions</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default ApexPlumbingDemo;
